import { useCallback, useEffect, useSyncExternalStore } from "react";
import { api } from "@/lib/apiClient";

/**
 * Persönliche Präferenz: welche Seitenbereiche (Filterleisten, Kachel-Gruppen …)
 * der Benutzer ausgeblendet hat. Quelle der Wahrheit ist `/me/ui-sections`;
 * ein localStorage-Spiegel sorgt dafür, dass die Seite ohne Flackern in der
 * zuletzt gewählten Ansicht rendert, bevor der Server geantwortet hat.
 */

const STORAGE_KEY = "eh:ui-sections-collapsed";

let collapsedSet: ReadonlySet<string> = readLocal();
let snapshot: string[] = [...collapsedSet];
let serverLoad: Promise<void> | null = null;
const listeners = new Set<() => void>();

function readLocal(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set();
  }
}

function writeLocal(next: ReadonlySet<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    // Privatmodus o. Ä. – Zustand lebt dann nur in dieser Sitzung.
  }
}

function commit(next: ReadonlySet<string>): void {
  collapsedSet = next;
  snapshot = [...next];
  writeLocal(next);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string[] {
  return snapshot;
}

/** Lädt den Serverstand einmal pro Sitzung (weitere Aufrufe teilen sich das Promise). */
export function ensureUiSectionsLoaded(): Promise<void> {
  if (!serverLoad) {
    serverLoad = api
      .get<{ collapsed?: unknown }>("/me/ui-sections")
      .then((data) => {
        const list = Array.isArray(data?.collapsed)
          ? data.collapsed.filter((v): v is string => typeof v === "string")
          : [];
        commit(new Set(list));
      })
      .catch(() => {
        // Ohne Server bleibt der lokale Spiegel; nächster Versuch beim nächsten Laden.
        serverLoad = null;
      });
  }
  return serverLoad;
}

export function isUiSectionCollapsed(key: string): boolean {
  return collapsedSet.has(key);
}

export async function setUiSectionCollapsed(key: string, collapsed: boolean): Promise<void> {
  const next = new Set(collapsedSet);
  if (collapsed) next.add(key);
  else next.delete(key);
  commit(next);
  try {
    const data = await api.put<{ collapsed?: unknown }>(
      `/me/ui-sections/${encodeURIComponent(key)}`,
      { collapsed },
    );
    if (Array.isArray(data?.collapsed)) {
      commit(new Set(data.collapsed.filter((v): v is string => typeof v === "string")));
    }
  } catch {
    // Lokale Auswahl bleibt aktiv; Server-Sync beim nächsten Laden.
  }
}

/** Blendet alle Bereiche wieder ein (persönliche Präferenz zurücksetzen). */
export async function resetUiSections(): Promise<void> {
  const previous = collapsedSet;
  commit(new Set());
  try {
    await api.del<{ collapsed?: unknown }>("/me/ui-sections");
  } catch {
    commit(previous);
    throw new Error("Zurücksetzen fehlgeschlagen.");
  }
}

/** Anzahl der aktuell ausgeblendeten Bereiche (reaktiv). */
export function useCollapsedUiSectionCount(): number {
  const keys = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    void ensureUiSectionsLoaded();
  }, []);
  return keys.length;
}

/**
 * Zustand eines ein-/ausblendbaren Bereichs. `key` ist ein stabiler, kleingeschriebener
 * Bezeichner mit Punkten als Trenner, z. B. `"projekte.filter"`.
 */
export function useCollapsibleSection(key: string) {
  const collapsedKeys = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const collapsed = collapsedKeys.includes(key);

  useEffect(() => {
    void ensureUiSectionsLoaded();
  }, []);

  const setCollapsed = useCallback(
    (next: boolean) => {
      void setUiSectionCollapsed(key, next);
    },
    [key],
  );

  const toggle = useCallback(() => {
    void setUiSectionCollapsed(key, !isUiSectionCollapsed(key));
  }, [key]);

  return { collapsed, setCollapsed, toggle };
}
